import React from 'react';
import { DocumentSheet } from '../../components/shared/folio/DocumentSheet';

/* The terms, drawn as a folio.

   The legal text is carried over word for word from the page this replaces;
   only its wrappers changed, from Heading/BodyText/BulletList to the plain
   prose the sheet styles. The section ids are the original ones, so any link
   already pointing into this document still lands where it did. */

const SECTIONS: [string, string][] = [
  ['definitions', "1. Definitions"],
  ['acceptance', "2. Acceptance of Terms"],
  ['eligibility', "3. Eligibility"],
  ['services', "4. Description of Services"],
  ['accounts', "5. User Accounts"],
  ['acceptable-use', "6. Acceptable Use"],
  ['content-ip', "7. Content & IP"],
  ['privacy', "8. Privacy & Data"],
  ['disclaimers', "9. Disclaimers"],
  ['liability', "10. Limitation of Liability"],
  ['indemnification', "11. Indemnification"],
  ['termination', "12. Termination"],
  ['disputes', "13. Dispute Resolution"],
  ['changes', "14. Changes to Terms"],
  ['governing-law', "15. Governing Law"],
  ['general', "16. General Provisions"],
  ['contact', "17. Contact Information"],
];

const TermsOfServicePage: React.FC = () => {
  return (
    <DocumentSheet
      title="Terms and Conditions"
      code="§ terms"
      dateline="Last updated: 21 February 2026"
      sections={SECTIONS}
      blockRows={[
        ['Sheet', 'TERMS'],
        ['Company', 'Acanetic Ltd'],
        ['Sections', String(SECTIONS.length)],
      ]}
    >

          {/* 1. Definitions and Interpretation */}
          <section id="definitions">
            <h2>
              1. Definitions and Interpretation
            </h2>
            <p>
              In these Terms and Conditions (the "<strong>Terms</strong>"), unless the context otherwise requires,
              the following definitions shall apply:
            </p>
            <ul>
                <li><strong>"Acanetic"</strong>, "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>" means Acanetic Ltd, a company registered in England and Wales, with its registered office at Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom.</li>
                <li><strong>"Platform"</strong> means the Acanetic website, web application, and all associated digital services operated by Acanetic Ltd, including all content, functionality, and features available therein.</li>
                <li><strong>"User"</strong>, "<strong>you</strong>", or "<strong>your</strong>" means any natural or legal person who accesses or uses the Platform, including job seekers, employers, and institutional representatives.</li>
                <li><strong>"Account"</strong> means the registered user account created by a User to access the features and services of the Platform.</li>
                <li><strong>"Content"</strong> means all text, data, information, documents, images, photographs, graphics, and other materials uploaded, posted, or otherwise made available by Users through the Platform.</li>
                <li><strong>"Services"</strong> means the job posting, job search, application management, professional networking, messaging, and career development features and resources provided through the Platform.</li>
                <li><strong>"Intellectual Property Rights"</strong> means all patents, copyrights, design rights, trade marks, database rights, trade secrets, know-how, and all other intellectual property rights, in each case whether registered or unregistered, and including all applications for, and renewals or extensions of, such rights.</li>
              
          </ul>
          </section>

          {/* 2. Acceptance of Terms */}
          <section id="acceptance">
            <h2>
              2. Acceptance of Terms
            </h2>
            <p>
              By accessing, browsing, or using the Platform, you acknowledge that you have read, understood,
              and agree to be bound by these Terms, together with our Privacy Policy, which is incorporated
              herein by reference. If you do not agree to these Terms in their entirety, you must immediately
              cease all use of the Platform.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Acanetic Ltd. We reserve the
              right to amend these Terms at any time in accordance with Section 14 below.
            </p>
          </section>

          {/* 3. Eligibility */}
          <section id="eligibility">
            <h2>
              3. Eligibility
            </h2>
            <p>
              The Platform is intended for use by individuals who are at least eighteen (18) years of age
              and who possess the legal capacity to enter into binding agreements. By using the Platform,
              you represent and warrant that you meet these eligibility requirements. If you are using the
              Platform on behalf of an organisation, you represent and warrant that you have the authority
              to bind that organisation to these Terms.
            </p>
          </section>

          {/* 4. Description of Services */}
          <section id="services">
            <h2>
              4. Description of Services
            </h2>
            <p>
              Acanetic is an online employment marketplace platform that connects academic professionals
              with employment opportunities at universities and research institutions. The Services provided
              through the Platform include, without limitation:
            </p>
            <ul>
                <li>Job posting and search functionality for academic and research positions;</li>
                <li>Professional networking features to facilitate connections between academic professionals and institutions;</li>
                <li>Application management tools enabling Users to submit, track, and manage job applications;</li>
                <li>Career development resources and information relevant to the academic employment sector.</li>
              
          </ul>
            <p>
              Acanetic acts solely as an intermediary platform. We do not act as an employer, recruitment
              agency, or employment adviser, and we do not guarantee or endorse any User, job listing, or
              employment outcome.
            </p>
          </section>

          {/* 5. User Accounts */}
          <section id="accounts">
            <h2>
              5. User Accounts
            </h2>
            <p>
              To access certain features of the Platform, you are required to create an Account. In connection
              with your Account, you agree to:
            </p>
            <ul>
                <li>Provide accurate, current, and complete information during the registration process and to update such information as necessary to ensure it remains accurate and complete;</li>
                <li>Maintain the confidentiality and security of your Account credentials, including your password, and not to disclose them to any third party;</li>
                <li>Accept full responsibility for all activities that occur under your Account, whether or not authorised by you;</li>
                <li>Notify us immediately at legal@acanetic.co.uk upon becoming aware of any unauthorised access to, or use of, your Account or any other breach of security.</li>
              
          </ul>
            <p>
              Acanetic shall not be liable for any loss or damage arising from your failure to comply with the
              obligations set out in this Section.
            </p>
          </section>

          {/* 6. Acceptable Use and User Conduct */}
          <section id="acceptable-use">
            <h2>
              6. Acceptable Use and User Conduct
            </h2>
            <p>
              You agree to use the Platform only for lawful purposes and in accordance with these Terms. Without
              limiting the generality of the foregoing, you shall not use the Platform to:
            </p>
            <ul>
                <li>Post, upload, or transmit any Content that is false, misleading, fraudulent, defamatory, obscene, or otherwise objectionable;</li>
                <li>Violate any applicable local, national, or international law, regulation, or directive, including without limitation data protection, employment, and anti-discrimination legislation;</li>
                <li>Infringe upon the Intellectual Property Rights or other proprietary rights of any third party;</li>
                <li>Harass, abuse, threaten, intimidate, or otherwise cause harm to any other User or third party;</li>
                <li>Attempt to gain unauthorised access to the Platform, its servers, databases, or any connected systems or networks;</li>
                <li>Use any automated system, software, bot, spider, scraper, or other technological means to access, extract data from, or interact with the Platform without our prior written consent;</li>
                <li>Interfere with or disrupt the integrity, performance, or availability of the Platform or the data contained therein;</li>
                <li>Impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity.</li>
              
          </ul>
            <p>
              We reserve the right, at our sole discretion, to investigate any suspected violation of this Section
              and to take appropriate action, including without limitation the removal of Content, suspension or
              termination of Accounts, and referral to law enforcement authorities.
            </p>
          </section>

          {/* 7. Content and Intellectual Property */}
          <section id="content-ip">
            <h2>
              7. Content and Intellectual Property
            </h2>
            <p>
              <strong>7.1 Your Content.</strong> You retain all ownership rights in and to the Content you upload,
              post, or submit to the Platform. By making Content available through the Platform, you grant to
              Acanetic a non-exclusive, worldwide, royalty-free, sublicensable, and transferable licence to use,
              reproduce, display, distribute, and otherwise exploit such Content solely in connection with the
              operation and provision of the Services, including the display of your profile and application
              materials to prospective employers. This licence shall terminate upon the deletion of the relevant
              Content from the Platform or the closure of your Account, except to the extent that copies of such
              Content have been shared with other Users in the ordinary course of the Services (e.g., job applications
              submitted to employers).
            </p>
            <p>
              <strong>7.2 Platform Intellectual Property.</strong> All Intellectual Property Rights in and to the Platform,
              including without limitation the source code, design, layout, graphics, trade marks, logos, and
              proprietary content (excluding User Content), are and shall remain the exclusive property of
              Acanetic Ltd. Nothing in these Terms grants you any right, title, or interest in the foregoing,
              except for the limited right to access and use the Platform in accordance with these Terms.
            </p>
            <p>
              <strong>7.3 Respect for Third-Party Rights.</strong> We respect the Intellectual Property Rights of others
              and expect our Users to do the same. If you believe that any Content available on the Platform
              infringes your Intellectual Property Rights, please notify us at legal@acanetic.co.uk with sufficient
              detail to enable us to identify and address the alleged infringement.
            </p>
          </section>

          {/* 8. Privacy and Data Protection */}
          <section id="privacy">
            <h2>
              8. Privacy and Data Protection
            </h2>
            <p>
              The protection of your personal data is of fundamental importance to us. Our collection, use,
              and protection of your personal data is governed by our Privacy Policy, which forms an integral
              part of these Terms and is available on the Platform. By using the Platform, you acknowledge that
              you have read and understood our Privacy Policy and that the processing of your personal data will
              be carried out in accordance therewith and with applicable data protection legislation, including
              the General Data Protection Regulation (GDPR), the UK GDPR, and the Data Protection Act 2018.
            </p>
          </section>

          {/* 9. Disclaimers */}
          <section id="disclaimers">
            <h2>
              9. Disclaimers
            </h2>
            <p>
              <strong>9.1</strong> The Platform and the Services are provided on an "<strong>as is</strong>" and
              "<strong>as available</strong>" basis, without warranties of any kind, whether express, implied,
              or statutory, including without limitation implied warranties of merchantability, fitness for a
              particular purpose, and non-infringement.
            </p>
            <p>
              <strong>9.2</strong> Without limiting the generality of the foregoing, Acanetic does not warrant or represent that:
            </p>
            <ul>
                <li>The information, job postings, or other Content made available through the Platform is accurate, complete, reliable, or current;</li>
                <li>Use of the Platform will result in successful job placements, hiring outcomes, or the identification of suitable candidates;</li>
                <li>The Platform will be available on an uninterrupted, timely, secure, or error-free basis;</li>
                <li>The Platform will be compatible with all hardware, software, devices, or browsers;</li>
                <li>Any defects or errors in the Platform will be corrected.</li>
              
          </ul>
            <p>
              <strong>9.3</strong> Nothing in these Terms shall exclude or limit any warranty implied by law that
              it would be unlawful to exclude or limit. Nothing in these Terms excludes or limits liability for
              death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for
              any other liability that cannot be excluded or limited under applicable law.
            </p>
          </section>

          {/* 10. Limitation of Liability */}
          <section id="liability">
            <h2>
              10. Limitation of Liability
            </h2>
            <p>
              <strong>10.1</strong> To the maximum extent permitted by applicable law, Acanetic Ltd, its directors,
              officers, employees, agents, and affiliates shall not be liable to you or to any third party for any
              indirect, incidental, special, consequential, exemplary, or punitive damages, howsoever arising,
              including without limitation:
            </p>
            <ul>
                <li>Loss of profits, revenue, or business;</li>
                <li>Loss of data or corruption of data;</li>
                <li>Loss of goodwill or reputation;</li>
                <li>Loss of anticipated savings;</li>
                <li>Any other indirect or consequential loss or damage;</li>
              
          </ul>
            <p>
              whether based on contract, tort (including negligence), strict liability, or any other legal theory,
              and whether or not Acanetic has been advised of the possibility of such damages.
            </p>
            <p>
              <strong>10.2</strong> Subject to Section 9.3, our total aggregate liability to you in respect of all claims
              arising out of or in connection with your use of the Platform or these Terms shall not exceed the
              greater of: (a) the total fees paid by you to Acanetic in the twelve (12) months immediately preceding
              the event giving rise to the claim; or (b) one hundred pounds sterling (&pound;100).
            </p>
            <p>
              <strong>10.3</strong> The limitations set out in this Section reflect the allocation of risk between the
              parties and form an essential basis of the bargain between us. The Services would not be provided
              without such limitations.
            </p>
          </section>

          {/* 11. Indemnification */}
          <section id="indemnification">
            <h2>
              11. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless Acanetic Ltd, its directors, officers, employees,
              agents, and affiliates from and against any and all claims, demands, losses, liabilities, damages,
              costs, and expenses (including reasonable legal fees) arising out of or in connection with: (a) your
              use of the Platform or the Services; (b) your breach of these Terms; (c) any Content you post,
              upload, or otherwise make available through the Platform; or (d) your violation of any applicable
              law, regulation, or third-party right.
            </p>
          </section>

          {/* 12. Termination */}
          <section id="termination">
            <h2>
              12. Termination
            </h2>
            <p>
              <strong>12.1 Termination by Acanetic.</strong> We may suspend or terminate your Account and your
              access to the Platform at any time, with or without prior notice, if we reasonably believe that
              you have violated these Terms, engaged in conduct that is harmful to the Platform or other Users,
              or for any other reason at our sole discretion. In the event of termination for cause, we shall
              endeavour to provide you with reasonable notice and an explanation of the grounds for termination,
              except where prohibited by law or where immediate action is necessary to protect the safety or
              integrity of the Platform.
            </p>
            <p>
              <strong>12.2 Termination by You.</strong> You may terminate your Account at any time by contacting us
              at legal@acanetic.co.uk or by using the account deletion functionality available through the
              Platform. Upon termination, your right to access and use the Platform shall cease immediately.
            </p>
            <p>
              <strong>12.3 Effects of Termination.</strong> Upon termination of your Account for any reason: (a) all
              licences granted to you under these Terms shall immediately terminate; (b) we may, but are not
              obligated to, delete your Content and Account data in accordance with our Privacy Policy and
              applicable data protection law; and (c) Sections 7 (to the extent of accrued obligations), 9, 10,
              11, 15, and 16 shall survive termination and continue in full force and effect.
            </p>
          </section>

          {/* 13. Dispute Resolution */}
          <section id="disputes">
            <h2>
              13. Dispute Resolution
            </h2>
            <p>
              In the event of any dispute arising out of or in connection with these Terms, the parties shall
              first attempt to resolve the matter through good-faith negotiation. If the dispute cannot be
              resolved through negotiation within thirty (30) days, either party may refer the matter to the
              courts in accordance with Section 15 below.
            </p>
          </section>

          {/* 14. Changes to Terms */}
          <section id="changes">
            <h2>
              14. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify, amend, or replace these Terms at any time. Where we make material
              changes to these Terms, we shall notify Users by email to the address associated with their Account,
              or by means of a prominent notice on the Platform, at least thirty (30) days prior to the changes
              taking effect. The updated Terms shall be posted on the Platform with a revised "Last updated" date.
              Your continued use of the Platform after the effective date of the revised Terms constitutes your
              acceptance of and agreement to be bound by the amended Terms. If you do not agree to the amended
              Terms, you must cease all use of the Platform and terminate your Account.
            </p>
          </section>

          {/* 15. Governing Law and Jurisdiction */}
          <section id="governing-law">
            <h2>
              15. Governing Law and Jurisdiction
            </h2>
            <p>
              <strong>15.1</strong> These Terms, and any dispute or claim arising out of or in connection with them
              or their subject matter or formation (including non-contractual disputes or claims), shall be
              governed by and construed in accordance with the laws of England and Wales.
            </p>
            <p>
              <strong>15.2</strong> The courts of England and Wales shall have non-exclusive jurisdiction to settle any
              dispute or claim arising out of or in connection with these Terms. This clause is without prejudice
              to the rights of consumers resident in the European Union to bring proceedings in the courts of
              their Member State of domicile in accordance with applicable EU consumer protection legislation,
              including Regulation (EU) No 1215/2012 (Brussels I Recast).
            </p>
            <p>
              <strong>15.3</strong> Nothing in these Terms shall affect any mandatory statutory rights that you may have
              as a consumer under the laws of your country of residence.
            </p>
          </section>

          {/* 16. General Provisions */}
          <section id="general">
            <h2>
              16. General Provisions
            </h2>
            <p>
              <strong>16.1 Entire Agreement.</strong> These Terms, together with the Privacy Policy, constitute the
              entire agreement between you and Acanetic in relation to your use of the Platform, and supersede
              all prior or contemporaneous agreements, representations, warranties, and understandings, whether
              written or oral, relating to the same subject matter.
            </p>
            <p>
              <strong>16.2 Severability.</strong> If any provision of these Terms is held to be invalid, illegal, or
              unenforceable by a court of competent jurisdiction, such provision shall be severed from these
              Terms, and the remaining provisions shall continue in full force and effect. The invalid or
              unenforceable provision shall be replaced by a valid and enforceable provision that most closely
              reflects the original intent of the parties.
            </p>
            <p>
              <strong>16.3 Waiver.</strong> No failure or delay by Acanetic in exercising any right, power, or privilege
              under these Terms shall operate as a waiver thereof, nor shall any single or partial exercise of
              any right, power, or privilege preclude any other or further exercise thereof or the exercise of
              any other right, power, or privilege.
            </p>
            <p>
              <strong>16.4 Assignment.</strong> You may not assign, transfer, or delegate your rights or obligations
              under these Terms without our prior written consent. Acanetic may assign its rights and obligations
              under these Terms to an affiliate or in connection with a merger, acquisition, or sale of all or
              substantially all of its assets.
            </p>
            <p>
              <strong>16.5 Force Majeure.</strong> Acanetic shall not be liable for any failure or delay in the
              performance of its obligations under these Terms to the extent that such failure or delay results
              from circumstances beyond its reasonable control, including without limitation acts of God, natural
              disasters, pandemics, epidemics, war, terrorism, civil unrest, government action, power failures,
              internet or telecommunications failures, cyberattacks, or labour disputes.
            </p>
            <p>
              <strong>16.6 Third-Party Rights.</strong> These Terms do not confer any rights on any person or party
              (other than the parties to these Terms) pursuant to the Contracts (Rights of Third Parties) Act 1999
              or otherwise.
            </p>
          </section>

          {/* 17. Contact Information */}
          <section id="contact">
            <h2>
              17. Contact Information
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding these Terms, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p>
                <strong>Email:</strong> legal@acanetic.co.uk<br />
                <strong>Postal Address:</strong> Acanetic Ltd, Cambridge Science Park, Cambridge, CB4 0WZ, United Kingdom<br />
                <strong>Telephone:</strong> +44 20 7123 4567
              </p>
            </div>
          </section>
    </DocumentSheet>
  );
};

export default TermsOfServicePage;
